import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  DailyTodoCardI,
  GetDailyTodosResponseI,
  SaveDailyTodoSettingsRequestI,
} from "@/api/routes/daily-todos";
import { useApiClient } from "@/context/api-client";

import {
  DailyTodosDataI,
  DailyTodosQueryKeys,
  isBadDailyTodoSettings,
  isDailyTodosSwitchedOff,
} from "../lib/data";
import { ET_TIMEZONE } from "../lib/schedule";

const fetchDailyTodos = async (
  api: ReturnType<typeof useApiClient>
): Promise<DailyTodosDataI> => {
  try {
    const { data } = await api.dailyTodos.getDailyTodos();
    const serverNow = data?.now ? Date.parse(data.now) : NaN;

    return {
      isSwitchedOn: true,
      todos: data ?? null,
      clockOffsetMs: Number.isNaN(serverNow) ? 0 : serverNow - Date.now(),
    };
  } catch (error) {
    if (isDailyTodosSwitchedOff(error)) {
      return { isSwitchedOn: false, todos: null, clockOffsetMs: 0 };
    }

    throw error;
  }
};

const isOn = (query: { state: { data?: DailyTodosDataI } }) =>
  query.state.data?.isSwitchedOn === true;

/**
 * The caller's To-Dos, and whether the feature is switched on (C-16 S6). A
 * switched-off backend is an answer: no nav item, no gate. Any other failure
 * is an error; the nav and the gate stay hidden (fail closed) and the page
 * keeps what it last showed, with a retry. One query serves the page, the
 * nav badge and the gate; pass `enabled` from useDailyTodosViewer so only
 * callers ask, and it polls only while the switch is on.
 */
export const useFetchDailyTodos = (enabled: boolean) => {
  const api = useApiClient();

  return useQuery({
    queryKey: DailyTodosQueryKeys.all(),
    queryFn: () => fetchDailyTodos(api),
    enabled,
    retry: false,
    staleTime: 60_000,
    // The day moves on (reminder time, snooze end, work done in the dialer).
    refetchInterval: (query) => (isOn(query) ? 5 * 60_000 : false),
    refetchOnWindowFocus: (query) => isOn(query),
  });
};

/**
 * Whether To-Dos is switched on, asked once per app load (LGAs' and Admins'
 * What's new card: they have no To-Dos of their own to poll).
 */
export const useFetchDailyTodosSwitch = (enabled: boolean) => {
  const api = useApiClient();

  return useQuery({
    queryKey: DailyTodosQueryKeys.switch(),
    queryFn: () => fetchDailyTodos(api),
    select: (data) => data.isSwitchedOn,
    enabled,
    retry: false,
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/** Marks a card worked in the cache at once; the server confirms on refetch. */
const markWorked = (
  data: DailyTodosDataI | undefined,
  cardKey: string
): DailyTodosDataI | undefined => {
  if (!data?.todos) {
    return data;
  }

  let changed = false;
  const sections = data.todos.sections.map((section) => {
    const cards = section.cards.map((card): DailyTodoCardI => {
      if (card.key !== cardKey || card.worked) {
        return card;
      }

      changed = true;

      return { ...card, worked: true, worked_source: "click" };
    });

    return {
      ...section,
      cards,
      worked: cards.filter((card) => card.worked).length,
    };
  });

  if (!changed) {
    return data;
  }

  const worked = data.todos.state.worked + 1;
  const todos: GetDailyTodosResponseI = {
    ...data.todos,
    sections,
    lifetime_worked: data.todos.lifetime_worked + 1,
    state: {
      ...data.todos.state,
      worked,
      complete: worked >= data.todos.state.total,
    },
  };

  return { ...data, todos };
};

const useInvalidate = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({ queryKey: DailyTodosQueryKeys.all() });
};

/** Ticks a card the caller opened from the list. */
export const useWorkDailyTodo = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (cardKey: string) => api.dailyTodos.workDailyTodo(cardKey),
    onMutate: async (cardKey) => {
      await queryClient.cancelQueries({ queryKey: DailyTodosQueryKeys.all() });
      const previous = queryClient.getQueryData<DailyTodosDataI>(
        DailyTodosQueryKeys.all()
      );

      queryClient.setQueryData<DailyTodosDataI | undefined>(
        DailyTodosQueryKeys.all(),
        (data) => markWorked(data, cardKey)
      );

      return { previous };
    },
    onError: (_error, _cardKey, context) => {
      if (context?.previous) {
        queryClient.setQueryData(DailyTodosQueryKeys.all(), context.previous);
      }
    },
    onSettled: () => void invalidate(),
  });
};

export const useSnoozeDailyTodos = () => {
  const api = useApiClient();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (until: Date) =>
      api.dailyTodos.snoozeDailyTodos(until.toISOString()),
    onSettled: () => invalidate(),
  });
};

export const useDismissDailyTodos = () => {
  const api = useApiClient();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: () => api.dailyTodos.dismissDailyTodos(),
    onSettled: () => invalidate(),
  });
};

export const useMarkDailyTodosBriefingSeen = () => {
  const api = useApiClient();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: () => api.dailyTodos.markDailyTodosBriefingSeen(),
    onSettled: () => invalidate(),
  });
};

export const useSaveDailyTodoSettings = () => {
  const api = useApiClient();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (body: SaveDailyTodoSettingsRequestI) =>
      api.dailyTodos.saveDailyTodoSettings(body),
    onSettled: () => invalidate(),
  });
};

export { markWorked as markDailyTodoWorkedInCache };

/**
 * Saves the daily time in the browser's zone; a zone the server refuses
 * (1313, e.g. an unusual IANA name) is saved in ET instead, so the caller is
 * never stuck. Used by the intro and the page's picker.
 */
export const useSaveDailyTime = () => {
  const save = useSaveDailyTodoSettings();

  const mutate = (
    body: SaveDailyTodoSettingsRequestI,
    handlers: { onSuccess: () => void; onError: (error: unknown) => void }
  ) =>
    save.mutate(body, {
      onSuccess: handlers.onSuccess,
      onError: (error) => {
        if (isBadDailyTodoSettings(error) && body.time_zone !== ET_TIMEZONE) {
          save.mutate(
            { ...body, time_zone: ET_TIMEZONE },
            { onSuccess: handlers.onSuccess, onError: handlers.onError }
          );

          return;
        }

        handlers.onError(error);
      },
    });

  return { mutate, isPending: save.isPending };
};
